import React, { useState, useEffect } from "react";
import { useAuth } from "../../lib/auth";
import { useContent } from "../../lib/content";
import { Field, TextArea, LinesField, ImageField, Msg, Section, IconSelect } from "../../components/admin/FormKit";
import { DEFAULT_ABOUT } from "../../lib/seed";
import { Save, BookOpen, Plus, Trash2 } from "lucide-react";

export default function AdminAbout() {
  const { r2 } = useAuth();
  const { about, saveAbout } = useContent();
  const [form, setForm] = useState(about);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({
      ...DEFAULT_ABOUT,
      ...about,
      intro_paragraphs: about.intro_paragraphs || DEFAULT_ABOUT.intro_paragraphs,
      values: about.values || DEFAULT_ABOUT.values,
    });
  }, [about]);

  const updateValue = (i, patch) => {
    setForm((f) => {
      const arr = [...(f.values || [])];
      arr[i] = { ...arr[i], ...patch };
      return { ...f, values: arr };
    });
  };
  const addValue = () => setForm((f) => ({ ...f, values: [...(f.values || []), { icon: "Leaf", title: "", desc: "" }] }));
  const removeValue = (i) => setForm((f) => ({ ...f, values: (f.values || []).filter((_, k) => k !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      await saveAbout(r2, form);
      setMsg("درباره ما ذخیره شد ✓");
    } catch (e) {
      setMsg(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-10" data-testid="admin-about-panel">
      <Section title="محتوای صفحه «درباره ما»" icon={BookOpen}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow" value={form.eyebrow} onChange={(v) => setForm({ ...form, eyebrow: v })} testid="admin-about-eyebrow" />
          <Field label="تیتر اصلی" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="admin-about-title" />
          <LinesField
            full
            rows={8}
            label="پاراگراف‌های معرفی (هر خط = یک پاراگراف)"
            value={form.intro_paragraphs}
            onChange={(v) => setForm({ ...form, intro_paragraphs: v })}
            testid="admin-about-intro"
          />
          <ImageField
            full
            label="تصویر اصلی صفحه"
            value={form.image}
            onChange={(v) => setForm({ ...form, image: v })}
            testid="admin-about-image"
          />
        </div>
      </Section>

      <Section title="ارزش‌های ما">
        <Field label="عنوان بخش ارزش‌ها" value={form.values_title} onChange={(v) => setForm({ ...form, values_title: v })} />
        <div className="space-y-4 mt-4">
          {(form.values || []).map((v, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 p-4 rounded-xl bg-honey-50/60">
              <div className="col-span-12 md:col-span-3">
                <IconSelect label="آیکون" value={v.icon} onChange={(x) => updateValue(i, { icon: x })} testid={`admin-value-icon-${i}`} />
              </div>
              <div className="col-span-12 md:col-span-4">
                <Field label="عنوان" value={v.title} onChange={(x) => updateValue(i, { title: x })} testid={`admin-value-title-${i}`} />
              </div>
              <div className="col-span-10 md:col-span-4">
                <Field label="توضیح" value={v.desc} onChange={(x) => updateValue(i, { desc: x })} testid={`admin-value-desc-${i}`} />
              </div>
              <div className="col-span-2 md:col-span-1 flex items-end">
                <button
                  type="button"
                  onClick={() => removeValue(i)}
                  className="w-full h-[42px] grid place-items-center bg-red-50 text-red-600 rounded-xl hover:bg-red-100"
                  data-testid={`admin-value-remove-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addValue}
            className="inline-flex items-center gap-1 text-honey-700 hover:text-honey-800 text-sm"
            data-testid="admin-value-add"
          >
            <Plus className="w-4 h-4" /> افزودن ارزش
          </button>
        </div>
      </Section>

      <div className="flex items-center justify-between sticky bottom-4 bg-paper/95 backdrop-blur border border-honey-200 rounded-full px-4 py-2 shadow-lg">
        <Msg msg={msg} />
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 bg-honey-500 hover:bg-honey-600 text-white font-display font-bold px-5 py-2.5 rounded-full disabled:opacity-60"
          data-testid="admin-about-save"
        >
          <Save className="w-4 h-4" /> {busy ? "در حال ذخیره..." : "ذخیره"}
        </button>
      </div>
    </form>
  );
}
